import { useMemo } from 'react';
import { Trash2 } from 'lucide-react';
import { NumberInput } from '@/components/molecules/NumberInput';
import { Switch } from '@/components/atoms/switch';
import { Button } from '@/components/atoms/button';
import { LineItemPicker } from '@/components/organisms/LineItemPicker';
import { usePricebookItems } from '@/hooks/pricebook/usePricebookItems';
import type { BookableServicePricebookItem } from '@/types/bookingConfig';

export interface EditableQuoteLine extends BookableServicePricebookItem {
  /** Display metadata — from the picker on add, or hydrated from the pricebook
   *  on edit. Never persisted; the server re-resolves items at quote time. */
  name?: string;
  unitPrice?: number;
}

const dollars = (amount: number) =>
  `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * The quoted pricebook lines on a bookable service: a pricebook search picker
 * over an editable rows table (quantity, optional toggle, remove). The public
 * wizard prices these lines live (with tax from the customer's address) and
 * required lines drive the service's default display price.
 */
export function BookableServiceLineItemsEditor({
  value,
  onChange,
}: {
  value: EditableQuoteLine[];
  onChange: (rows: EditableQuoteLine[]) => void;
}) {
  // Hydrate names/prices for rows loaded from a saved service (id-only refs).
  const missingIds = value.filter((r) => r.name == null).map((r) => r.lineItemId);
  const { data: hydration } = usePricebookItems(
    { ids: missingIds, limit: Math.max(missingIds.length, 1) },
    { enabled: missingIds.length > 0, showErrorToast: false },
  );
  const hydrated = useMemo(() => {
    if (!hydration?.items?.length) return value;
    const byId = new Map(hydration.items.map((item) => [item.id, item]));
    return value.map((row) => {
      if (row.name != null) return row;
      const item = byId.get(row.lineItemId);
      return item ? { ...row, name: item.name, unitPrice: item.price } : row;
    });
  }, [value, hydration]);

  const requiredTotal = hydrated.reduce(
    (sum, row) => (row.isOptional ? sum : sum + (row.unitPrice ?? 0) * row.quantity),
    0,
  );

  const patch = (lineItemId: string, changes: Partial<EditableQuoteLine>) =>
    onChange(hydrated.map((row) => (row.lineItemId === lineItemId ? { ...row, ...changes } : row)));

  return (
    <div className="space-y-2">
      <LineItemPicker
        onAdd={(item) => {
          if (hydrated.some((row) => row.lineItemId === item.lineItemId)) return;
          onChange([
            ...hydrated,
            {
              lineItemId: item.lineItemId,
              quantity: item.quantity > 0 ? item.quantity : 1,
              isOptional: false,
              name: item.name,
              unitPrice: item.basePrice,
            },
          ]);
        }}
        excludeIds={hydrated.map((row) => row.lineItemId)}
        allowedTypes={['SERVICE', 'MATERIAL', 'EQUIPMENT']}
        allowCustomItems={false}
        placeholder="Search the pricebook…"
      />

      {hydrated.length > 0 && (
        <>
          <ul className="divide-y rounded-md border">
            {/* The name column is the only flexible one, so it must be allowed to
                shrink (min-w-0) while the controls keep their intrinsic width —
                otherwise the row forces the settings dialog to scroll sideways
                and clips the "Optional" toggle. */}
            {hydrated.map((row) => (
              <li
                key={row.lineItemId}
                className="flex min-w-0 items-center gap-2 px-3 py-2 text-sm"
              >
                <span className="min-w-0 flex-1 truncate">
                  {row.name ?? 'Pricebook item'}
                  {row.unitPrice != null && (
                    <span className="ml-1 text-xs text-muted-foreground">
                      {dollars(row.unitPrice)}
                    </span>
                  )}
                </span>
                <NumberInput
                  min="0.01"
                  max="9999"
                  step="0.01"
                  className="h-8 w-16 shrink-0"
                  aria-label="Quantity"
                  value={row.quantity}
                  emptyValue={1}
                  onChange={(next) =>
                    // Clamp into the server contract (0.01–9999) so a typed 0,
                    // negative, or absurd value can't reach the DTO and 400.
                    patch(row.lineItemId, {
                      quantity: Math.min(9999, Math.max(0.01, next ?? 1)),
                    })
                  }
                />
                <label
                  className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground"
                  title="Optional add-on the customer can select"
                >
                  <Switch
                    checked={row.isOptional === true}
                    onCheckedChange={(v) => patch(row.lineItemId, { isOptional: v })}
                    aria-label="Optional add-on"
                  />
                  <span className="hidden sm:inline">Optional</span>
                </label>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  aria-label="Remove line"
                  onClick={() => onChange(hydrated.filter((r) => r.lineItemId !== row.lineItemId))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground">
            Required lines total {dollars(requiredTotal)} — the service&apos;s default display
            price. Customers see these lines itemized with tax for their address; optional lines are
            add-ons they can select.
          </p>
        </>
      )}
    </div>
  );
}
